import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import type { MultiSelectPassThroughOptions } from "primereact/multiselect";
import { classNames } from "primereact/utils";

import { Calendar } from "../../../calendar";
import { Dropdown } from "../../../dropdown";
import { primeIcon } from "../../../icon";
import { MonthRangePicker } from "../../../month-range-picker";
import { MultiSelect } from "../../../multi-select";
import { VALUES_LIST_SCROLL_HEIGHT } from "../../filter-panel.constants";
import styles from "../../filter-panel.module.scss";
import {
  DATE_END_PLACEHOLDER,
  DATE_PLACEHOLDER,
  FILTER_NO_RESULTS_MESSAGE,
  MONTH_RANGE_PLACEHOLDER,
  VALUES_PLACEHOLDER,
} from "../../messages";
import type { FilterPanelRowViewProps, FilterRowControlViewProps } from "./types-filter-row";

const CALENDAR_ICON = primeIcon("calendar", 14);
const TRASH_ICON = primeIcon("trash", 14);

// Lista de valores do multiselect (Figma Filtro/Lista 11464:764224): sem seletor de todos nem fechar.
const VALUES_PANEL_PT = {
  header: { className: styles.valuesHeader },
  closeButton: { className: styles.valuesClose },
  filterContainer: { className: styles.valuesFilterContainer },
  // O Prime repassa `filterInput` como `pt` do `InputText`, que lê a seção `root`; o `.d.ts` o tipa como atributos diretos.
  filterInput: {
    root: { className: styles.valuesFilter },
  } as MultiSelectPassThroughOptions["filterInput"],
};

function FilterRowControlView({
  control,
  controlTestId,
  base,
  invalid,
}: FilterRowControlViewProps) {
  switch (control.kind) {
    case "select":
      return (
        <Dropdown
          filter
          value={control.value}
          options={control.options}
          onChange={control.onChange}
          placeholder={base.placeholder}
          emptyMessage={base.emptyMessage}
          emptyFilterMessage={FILTER_NO_RESULTS_MESSAGE}
          filterPlaceholder={base.searchPlaceholder}
          disabled={base.disabled}
          invalid={invalid}
          aria-label={base.ariaLabel}
          data-testid={controlTestId}
        />
      );
    case "multiselect":
      return (
        <MultiSelect
          filter
          showSelectAll={false}
          panelClassName={styles.valuesPanel}
          scrollHeight={VALUES_LIST_SCROLL_HEIGHT}
          pt={VALUES_PANEL_PT}
          value={control.value}
          options={control.options}
          onChange={control.onChange}
          placeholder={base.placeholder}
          emptyMessage={base.emptyMessage}
          emptyFilterMessage={FILTER_NO_RESULTS_MESSAGE}
          filterPlaceholder={base.searchPlaceholder}
          disabled={base.disabled}
          invalid={invalid}
          aria-label={base.ariaLabel}
          data-testid={controlTestId}
        />
      );
    case "date":
      return (
        <>
          <Dropdown
            value={control.mode}
            options={control.modeOptions}
            onChange={control.onModeChange}
            placeholder={VALUES_PLACEHOLDER}
            invalid={invalid && control.mode === null}
            aria-label={base.ariaLabel}
          />
          {control.mode ? (
            <div className={styles.dateInput} data-testid={controlTestId}>
              <Calendar
                showIcon={false}
                className={styles.dateCalendar}
                value={control.start}
                onChange={control.onStartChange}
                placeholder={DATE_PLACEHOLDER}
                invalid={invalid}
                aria-label={base.ariaLabel}
              />
            </div>
          ) : null}
          {control.mode === "period" ? (
            <div className={styles.dateInput}>
              <Calendar
                showIcon={false}
                className={styles.dateCalendar}
                value={control.end}
                onChange={control.onEndChange}
                minDate={control.start ?? undefined}
                disabled={!control.start}
                placeholder={DATE_END_PLACEHOLDER}
                invalid={invalid}
                aria-label={DATE_END_PLACEHOLDER}
              />
            </div>
          ) : null}
        </>
      );
    case "date-range":
      return (
        <div data-testid={controlTestId}>
          <Calendar
            selectionMode="range"
            value={control.value}
            onChange={control.onChange}
            invalid={invalid}
            aria-label={base.ariaLabel}
          />
        </div>
      );
    case "month-range":
      return control.empty ? (
        <Button
          type="button"
          icon={CALENDAR_ICON}
          label={MONTH_RANGE_PLACEHOLDER}
          outlined
          severity="secondary"
          disabled={base.disabled}
          aria-label={base.ariaLabel}
          onClick={control.start}
          data-testid={controlTestId}
        />
      ) : (
        <div data-testid={controlTestId}>
          <MonthRangePicker
            value={control.value}
            onChange={control.onChange}
            invalid={invalid}
            aria-label={base.ariaLabel}
          />
        </div>
      );
    case "text":
      return (
        <InputText
          value={control.value}
          onChange={control.onChange}
          aria-label={base.ariaLabel}
          aria-invalid={invalid || undefined}
          data-testid={controlTestId}
        />
      );
    default:
      return null;
  }
}

export function FilterPanelRowView({
  testId,
  className,
  rest,
  field,
  control,
  controlTestId,
  base,
  invalid,
  error,
  errorTestId,
  remove,
}: FilterPanelRowViewProps) {
  return (
    <div className={classNames(styles.row, className)} data-testid={testId} {...rest}>
      <div
        className={classNames(styles.rowFields, control.kind === "date" && styles.rowFieldsDate)}
      >
        <Dropdown
          filter
          value={field.value}
          options={field.options}
          onChange={field.onChange}
          placeholder={field.placeholder}
          emptyFilterMessage={FILTER_NO_RESULTS_MESSAGE}
          aria-label={field.placeholder}
          data-testid={field.testId}
        />
        <FilterRowControlView
          control={control}
          controlTestId={controlTestId}
          base={base}
          invalid={invalid}
        />
        {error ? (
          <small role="alert" className={styles.error} data-testid={errorTestId}>
            {error}
          </small>
        ) : null}
      </div>
      <Button
        type="button"
        icon={TRASH_ICON}
        text
        aria-label={remove.label}
        onClick={remove.onClick}
        data-testid={remove.testId}
      />
    </div>
  );
}
